import { inject, Injectable } from '@angular/core';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { delayWhen, EMPTY, filter, map, Observable, of } from 'rxjs';

import { ElementsState, runtimeSelectors } from '@blaze-runtime/engine-data-access';
import {
  CanvasElement,
  CanvasElementEvent,
  CanvasElementId,
  CanvasElementType,
  IoConfig,
  RuntimeEventQueryData,
  RuntimeUiEventPayload,
} from '@blaze-runtime/engine-domain';
import {
  BULK_ACTION_IDS_PROP,
  ElementQueryState,
  isWizardMetaData,
  PaginationDirection,
  RenderElementDataRowContext,
  WizardElementMetaDataFeatures,
} from '@blaze-shared/elements-domain';
import { isActionEventType } from '@blaze-shared/schema-domain';
import { type ElementSchema } from '@blaze-shared/schema-domain';
import { optionalCast } from '@blaze-shared/shared-util';

import { ElementsFacade } from '../../../+state/elements/elements.facade';
import { isFormGroupLike } from '../../element-model/element-type-guards';
import {
  BuildOutputStreamConfig,
  ElementOutputEvent,
  ElementOutputStreamValue,
} from '../models/output-handler.models';
import { ComponentValidationHandler } from '../models/validation-handler.models';

import { ElementOutputSchemaService } from './element-output-schema.service';
import { FormGroupOutputSynchronizer } from './form-group-output-synchronizer';

/**
 * Kept for backward compat - external code imports DefaultDataOutputStream from here.
 * New code should import ElementOutputStreamValue from models/output-handler.models.
 */
export type DefaultDataOutputStream = ElementOutputStreamValue;

/** Private config for the internal raw stream builder. */
interface RawStreamConfig {
  elementInstanceOutput: Observable<CanvasElementEvent>;
  element: CanvasElement;
  validationHandler: ComponentValidationHandler | undefined;
}

/** Private config for applyEventContextToPayload. */
interface ApplyEventContextConfig {
  payload: RuntimeUiEventPayload;
  event: CanvasElementEvent;
  element: CanvasElement;
  data: any;
  isBulkAction: boolean;
}

@Injectable({ providedIn: 'root' })
export class ElementOutputStreamBuilder {
  private readonly elementOutputSchemaService = inject(ElementOutputSchemaService);
  private readonly elementsFacade = inject(ElementsFacade);
  private readonly formGroupOutputSynchronizer = inject(FormGroupOutputSynchronizer);
  private readonly store = inject(Store<ElementsState>);

  getElementOutputEventStream({
    element,
    elementDefinition,
    elementInstance,
    rowContext$,
    validationHandler,
    isDataContainerValueSetNotifier,
    outputEmitGuard,
  }: BuildOutputStreamConfig): Observable<ElementOutputEvent> {
    const elementInstanceOutput = elementInstance.output;

    if (!elementInstanceOutput) {
      return EMPTY;
    }
    return this.buildRawStream({
      elementInstanceOutput,
      element,
      validationHandler,
    }).pipe(
      concatLatestFrom(() => rowContext$),
      map(([{ event, element: currentElement, pagination, elements, wizardMeta }, rowContext]) => {
        if (!currentElement) {
          return;
        }
        this.assertOutputEventTypeAllowed(currentElement, elementDefinition?.ioConfig, event);

        const shouldEmit = outputEmitGuard
          ? outputEmitGuard.preEmit({
              event,
              element: currentElement,
              validationHandler,
              elements,
              wizardMeta,
              isDataContainerValueSetNotifier,
            })
          : true;
        if (!shouldEmit) {
          return;
        }

        // isBulkAction = true: only table
        const [data, workflowId, isBulkAction] = !isActionEventType(event.eventType)
          ? [event.data, undefined, false]
          : [event.data.data, event.data.workflowId, event.data.isBulkAction];

        this.adjustInitialPaginationDirection(event, pagination, data);

        const payload = this.buildEventPayload(
          currentElement,
          event,
          rowContext,
          workflowId,
          elementInstance.getDefaultEventSchema?.(event.eventType),
        );

        this.applyEventContextToPayload({
          payload,
          event,
          element: currentElement,
          data,
          isBulkAction,
        });

        return { event, uiEvent: payload };
      }),
    );
  }

  private getWizardMeta$(
    elementId: CanvasElementId,
    elementType: CanvasElementType,
  ): Observable<WizardElementMetaDataFeatures | undefined> {
    if (elementType !== 'wizard') {
      return of(undefined);
    }
    return this.store
      .select(runtimeSelectors.selectElementMetaDataFeaturesById(elementId))
      .pipe(optionalCast(isWizardMetaData));
  }

  private buildRawStream({
    elementInstanceOutput,
    element,
    validationHandler,
  }: RawStreamConfig): Observable<ElementOutputStreamValue> {
    const includeWizardMeta = element.type === 'wizard';
    return elementInstanceOutput.pipe(
      concatLatestFrom(() => [
        this.elementsFacade.getElement(element.id),
        this.store.select(runtimeSelectors.selectQueryByElementId(element.id)),
        this.elementsFacade.entities$,
        includeWizardMeta ? this.getWizardMeta$(element.id, element.type) : of(undefined),
      ]),
      map(
        ([event, element, pagination, elements, wizardMeta]) =>
          ({
            event,
            element,
            pagination,
            elements,
            wizardMeta,
          }) as ElementOutputStreamValue,
      ),
      filter(({ element }) => !!element),
      delayWhen(({ event, element: streamElement, elements }) =>
        this.formGroupOutputSynchronizer.waitForFormGroupsInitialized(
          streamElement,
          event,
          elements,
          validationHandler?.getValidationData(element.id)?.buttonFormGroupData,
        ),
      ),
    );
  }

  /** Throws if the emitted event type is not in the element's ioConfig. */
  private assertOutputEventTypeAllowed(
    element: CanvasElement,
    ioConfig: IoConfig | undefined,
    event: CanvasElementEvent,
  ): void {
    const elOutput = ioConfig?.outputs?.find((output) => output.eventType === event.eventType);
    if (!elOutput) {
      throw new Error(
        `Event type (${event.eventType}) not allowed for element (id: ${element.id})`,
      );
    }
  }

  /** For initial query events without prior data, overrides direction to `$Initial`. */
  private adjustInitialPaginationDirection(
    event: CanvasElementEvent,
    pagination: ElementQueryState | undefined,
    data: any,
  ): void {
    if (
      event.eventType === 'query' &&
      event.output !== 'data-filter' &&
      pagination?.initialDataReceived === false &&
      (data as RuntimeEventQueryData | undefined)?.page?.direction === PaginationDirection.First
    ) {
      data.page.direction = PaginationDirection.$Initial;
    }
  }

  private buildEventPayload(
    element: CanvasElement,
    event: CanvasElementEvent,
    rowContext: RenderElementDataRowContext | undefined,
    workflowId: string | undefined,
    elementSchema: ElementSchema | null | undefined,
  ): RuntimeUiEventPayload {
    return {
      data: undefined,
      elementId: element.id,
      eventType: event.eventType,
      isInitialData: event.isInitialData,
      ns: element.ns,
      output: event.output,
      schema:
        event.schema ||
        this.elementOutputSchemaService.getElementOutputSchema(
          element,
          event.eventType,
          elementSchema,
        ),
      workflowId,
      rowContext,
      updateSource: event.updateSource,
    };
  }

  private applyEventContextToPayload({
    payload,
    event,
    element,
    data,
    isBulkAction,
  }: ApplyEventContextConfig): void {
    if (isBulkAction) {
      payload.eventContext = { [BULK_ACTION_IDS_PROP]: data, dataAction: true };
    } else {
      payload.data = isFormGroupLike(element) && Array.isArray(data) ? data[0] : data;
      if (isActionEventType(event.eventType)) {
        payload.eventContext = { dataAction: true };
      }
      if (event.eventType === 'row-select' && event.isEmptyishData) {
        payload.eventContext = { isEmptyDataSelection: true };
      }
    }
    if (event.userUpdate) {
      payload.eventContext = { ...payload.eventContext, userUpdate: event.userUpdate };
    }
    if (event.preventConnectionsTrigger) {
      payload.eventContext = {
        ...payload.eventContext,
        preventConnectionsTrigger: event.preventConnectionsTrigger,
      };
    }
  }
}
